import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, ArrowRight, Sparkles, Clock3 } from "lucide-react";
import ServicePrice from "../components/ServicePrice";
import { useCatalog } from "../contexts/CatalogContext";

export default function Pricing() {
  const { categories, AMC_PLANS } = useCatalog();
  const [activeCat, setActiveCat] = useState(categories[0]?.id || "");
  const cat = categories.find((c) => c.id === activeCat) || categories[0] || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Pricing</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3">Simple, transparent pricing</h1>
        <p className="mt-4 text-slate-600">"Starting from" rates cover labour for the listed scope. Spares billed at MRP after your approval.</p>
      </div>

      {/* No hidden charges banner */}
      <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-xl p-4 flex items-center justify-center gap-3 mb-10" data-testid="no-hidden-banner">
        <ShieldCheck className="w-5 h-5 text-[#22C55E]" />
        <span className="text-sm font-semibold text-slate-800">No hidden charges. No advance. Pay only after the job is done.</span>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="pricing-tabs">
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`pricing-tab-${c.id}`}
            onClick={() => setActiveCat(c.id)}
            aria-pressed={cat.id === c.id}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              cat.id === c.id ? "bg-[#0C4A6E] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#0C4A6E]"
            }`}
          >
            {c.short}
          </button>
        ))}
      </div>

      <section aria-label={`${cat.name || "Service"} pricing`}>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h2 className="font-display font-bold text-2xl text-slate-900">{cat.name || "Services"}</h2>
            <p className="mt-2 text-sm text-slate-500">Expert care. Clear prices. Choose what you need.</p>
          </div>
          {!!cat.brands?.length && <span className="text-xs font-medium text-slate-500">{cat.brands.length} brands supported</span>}
        </div>
        {cat.status === "soon" || !cat.services?.length ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <h3 className="font-semibold text-lg text-slate-900">{cat.status === "soon" ? "Coming soon" : "No services available yet"}</h3>
            <p className="mt-2 text-sm text-slate-500">Please explore our other service categories.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`pricing-table-${cat.id}`}>
            {cat.services.map((s) => (
              <article key={s.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-lg hover:shadow-sky-900/5">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.bg, color: cat.color }}>
                    {cat.icon && <cat.icon className="w-6 h-6" aria-hidden="true" />}
                  </div>
                  {s.time && <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Clock3 size={14} aria-hidden="true" />{s.time}</span>}
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                <div className="mt-6">
                  <p className="text-xs text-slate-500 mb-1.5">Starting from</p>
                  <div className="font-display text-3xl text-[#0C4A6E]">
                    <ServicePrice service={s} />
                    {s.id.includes("amc") && <span className="ml-1 text-xs font-medium text-slate-500">/year</span>}
                  </div>
                </div>
                {!!s.includes?.length && <ul className="mt-5 space-y-2 border-t border-slate-100 pt-5">
                  {s.includes.map((item) => <li key={item} className="flex items-start gap-2 text-xs leading-5 text-slate-600"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" />{item}</li>)}
                </ul>}
                <div className="mt-auto pt-6">
                  <Link to={`/booking?category=${cat.id}&service=${s.id}`} data-testid={`pricing-book-${s.id}`} className="flex items-center justify-between gap-2 rounded-xl bg-[#0369A1] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#075985]">
                    Book service <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Segment pricing note */}
      <p className="text-xs text-slate-500 mt-4 text-center" data-testid="segment-pricing-note">
        Shop / office rates may differ for bulk units and commercial contracts. Get a custom quote on{" "}
        <Link to="/contact" className="text-[#0369A1] font-semibold">Contact</Link>.
      </p>

      {/* AMC Plans */}
      <section className="mt-20" data-testid="amc-plans-section">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">AMC Plans</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Annual Maintenance — Peace of mind, year-round</h2>
          <p className="mt-3 text-slate-600">Applicable to RO, AC and Washing Machine. Per appliance, per year.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {AMC_PLANS.map((p) => (
            <div
              key={p.name}
              data-testid={`amc-plan-${p.name.toLowerCase()}`}
              className={`relative rounded-2xl p-7 border-2 ${
                p.popular ? "border-[#0369A1] bg-white shadow-xl" : "border-slate-200 bg-white"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 right-6 bg-[#0369A1] text-white text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <div className="font-display font-bold text-xl text-slate-900">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-extrabold text-4xl text-[#0C4A6E]"><ServicePrice service={p} /></span>
                <span className="text-sm text-slate-500">/year</span>
              </div>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-[#22C55E] mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/booking"
                data-testid={`amc-select-${p.name.toLowerCase()}`}
                className={`mt-7 block text-center font-semibold py-3 rounded-xl transition-all ${
                  p.popular
                    ? "bg-[#0369A1] hover:bg-[#075985] text-white"
                    : "bg-[#0C4A6E] hover:bg-[#132D53] text-white"
                }`}
              >
                Choose {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
